import { useRef, useState } from 'react';
import { Button, Choice, Destructive, Icon, Input, Modal, Slider, useToast } from '@efira/ui';

import {
  useClearMusic,
  useMusic,
  useMusicCommand,
  useMusicRightsSeen,
  useRemoveTrack,
  useRenameTrack,
  useReorderMusic,
  useUploadTrack,
} from '../../api/queries';
import { useConsoleStream } from '../stream';

/**
 * Музыка: список стримера и то, что сейчас играет.
 *
 * МУЗЫКА ЗВУЧИТ В ОВЕРЛЕЕ. Здесь только управление и отражение состояния:
 * что играет, где мы в треке и идёт ли воспроизведение, приходит ОТ ОВЕРЛЕЯ
 * (`stage:state`). Полоса, считающая время сама, разошлась бы с эфиром на
 * первой же паузе - и стример узнал бы об этом от зрителей.
 *
 * ТРЕТЬЕЙ ВКЛАДКИ ЭТАЛОНА («AI SEARCH») У НАС НЕТ, и пустого места от неё не
 * остаётся: две вкладки - «Что играет» и «Моя музыка» (SPEC 1.1).
 */
export function Music() {
  const music = useMusic();
  const stream = useConsoleStream();
  const command = useMusicCommand();
  const toast = useToast();
  const [tab, setTab] = useState<'now' | 'list'>('now');

  const tracks = music.data?.tracks ?? [];
  const now = stream.music;
  const current = tracks.find((track) => track.id === now?.trackId) ?? null;

  const send = (action: string, extra: Record<string, unknown> = {}) => {
    command.mutate({ action, ...extra } as { action: string });
  };

  const index = current === null ? -1 : tracks.indexOf(current);

  return (
    <div data-right="music" className="flex h-full min-h-0 flex-col">
      {/* Проигрыватель ВСЕГДА сверху, как у эталона: что играет и чем
          управлять, видно в обеих вкладках, а не только в одной. */}
      <NowPlaying
        name={now?.name ?? current?.name ?? null}
        playing={now?.playing === true}
        position={now?.position ?? 0}
        duration={now?.duration ?? 0}
        onSeek={(position) => {
          send('seek', { position });
        }}
        onToggle={() => {
          if (now?.playing === true) {
            send('pause');
            return;
          }

          const track = current ?? tracks[0];

          if (track === undefined) {
            toast.show({
              tone: 'warning',
              title: 'Играть нечего',
              text: 'Загрузите трек во вкладке «Моя музыка».',
            });
            return;
          }

          send('play', { trackId: track.id });
        }}
        onStep={(step) => {
          const next = tracks[(index + step + tracks.length) % Math.max(1, tracks.length)];

          if (next !== undefined) {
            send('play', { trackId: next.id });
          }
        }}
      />

      <div data-control-row="music-head" className="@container flex items-center gap-1 px-3 pb-2">
        <Choice
          label="Разделы музыки"
          value={tab}
          onChange={setTab}
          size="sm"
          className="shrink-0"
          options={[
            { value: 'now', label: 'Что играет', data: { 'data-music-tab': 'now' } },
            { value: 'list', label: 'Моя музыка', data: { 'data-music-tab': 'list' } },
          ]}
        />
      </div>

      <div
        data-console-scroll="music"
        tabIndex={0}
        role="region"
        aria-label="Музыка"
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
      >
        {tab === 'now' ? (
          <p className="text-foreground/80 text-sm">
            Музыка играет в оверлее — там, где её слышит эфир. Громкость — канал «Музыка игры» в
            микшере. Список и загрузка — во вкладке «Моя музыка».
          </p>
        ) : (
          <Playlist
            onPlay={(id) => {
              send('play', { trackId: id });
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Что играет: название, перемотка и четыре кнопки. */
function NowPlaying({
  name,
  playing,
  position,
  duration,
  onSeek,
  onToggle,
  onStep,
}: {
  name: string | null;
  playing: boolean;
  position: number;
  duration: number;
  onSeek: (position: number) => void;
  onToggle: () => void;
  onStep: (step: number) => void;
}) {
  const clock = (value: number) =>
    `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;

  return (
    <div className="flex flex-col gap-2">
      <p data-music="now" className="text-foreground truncate text-sm font-semibold">
        {name ?? 'Ничего не играет'}
      </p>

      <Slider
        data-music="seek"
        aria-label="Перемотка"
        min={0}
        max={Math.max(duration, 1)}
        value={[Math.min(position, duration)]}
        disabled={duration === 0}
        onValueChange={(next) => {
          onSeek(next[0] ?? 0);
        }}
      />

      <div className="text-foreground/80 flex justify-between text-xs tabular-nums">
        <span>{clock(position)}</span>
        <span>{clock(duration)}</span>
      </div>

      <div
        role="group"
        aria-label="Управление музыкой"
        data-music="transport"
        className="flex items-center justify-center gap-1"
      >
        <Button
          variant="ghost"
          size="s"
          data-music-control="prev"
          aria-label="Предыдущий трек"
          onClick={() => {
            onStep(-1);
          }}
        >
          <Icon name="chevronLeft" size="sm" />
        </Button>

        <Button
          variant="primary"
          size="s"
          data-music-control={playing ? 'pause' : 'play'}
          aria-label={playing ? 'Пауза' : 'Играть'}
          onClick={onToggle}
        >
          <Icon name={playing ? 'pause' : 'play'} size="sm" />
        </Button>

        <Button
          variant="ghost"
          size="s"
          data-music-control="next"
          aria-label="Следующий трек"
          onClick={() => {
            onStep(1);
          }}
        >
          <Icon name="chevronRight" size="sm" />
        </Button>

        <Button
          variant="ghost"
          size="s"
          data-music-control="stop"
          aria-label="Остановить"
          onClick={() => {
            onSeek(0);
            onStep(0);
          }}
        >
          <Icon name="stop" size="sm" />
        </Button>
      </div>

      <p className="text-foreground/80 text-sm">
        Музыка играет в оверлее — там, где её слышит эфир. Громкость — канал «Музыка игры» в
        микшере.
      </p>
    </div>
  );
}

/** Своя музыка: загрузка, порядок, переименование, удаление. */
function Playlist({ onPlay }: { onPlay: (id: string) => void }) {
  const music = useMusic();
  const uploadTrack = useUploadTrack();
  const rename = useRenameTrack();
  const remove = useRemoveTrack();
  const clear = useClearMusic();
  const reorder = useReorderMusic();
  const seen = useMusicRightsSeen();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  /* Признак ВЫВОДИТСЯ, а не ставится эффектом: окно показывается, пока
     сервер не знает, что человек его видел, и пока он сам не закрыл. */
  const [dismissed, setDismissed] = useState(false);

  const tracks = music.data?.tracks ?? [];
  const maxMb = Math.round((music.data?.maxBytes ?? 20 * 1024 * 1024) / 1024 / 1024);

  /* Предупреждение о правах показывается ОДИН РАЗ, при первой загрузке:
     стример узнаёт о своей ответственности от нас заранее, а не от
     площадки постфактум. */
  const rights = !dismissed && music.data?.rightsSeen === false && tracks.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div data-control-row="music-playlist-head" className="@container flex items-center gap-2">
        <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
          Мои треки · {tracks.length} из {music.data?.limit ?? 30}
        </span>

        {/* eslint-disable-next-line no-restricted-syntax -- скрытый файловый ввод: органа управления на экране нет, его открывает кнопка рядом. У пресета компонента для файла нет */}
        <input
          ref={input}
          type="file"
          data-music="upload-input"
          accept="audio/mpeg,audio/ogg,audio/wav,.mp3,.ogg,.wav"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';

            if (file === undefined) {
              return;
            }

            uploadTrack.mutate(file, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: 'Трек добавлен' });
              },
            });
          }}
        />

        {/* Порядок как у эталона: сперва сброс, последним - основное. */}
        {tracks.length > 0 && (
          <Destructive
            id="playlist-clear"
            className="shrink-0"
            data={{ 'data-music': 'clear' }}
            label="Очистить"
            title="Очистить список?"
            confirm="Очистить"
            onConfirm={() => {
              clear.mutate(undefined, {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Список очищен' });
                },
              });
            }}
          >
            Все {tracks.length} треков уйдут из списка. Сами файлы останутся у вас на компьютере.
          </Destructive>
        )}

        {/* Не главная: главная кнопка консоли одна и снимает блокировку
            эфира (блок 2 единицы 21). Загрузка трека - из дополнительного. */}
        <Button
          variant="secondary"
          size="s"
          className="shrink-0 gap-2"
          data-music="add"
          loading={uploadTrack.isPending}
          onClick={() => {
            input.current?.click();
          }}
        >
          <Icon name="upload" size="sm" />
          Загрузить
        </Button>
      </div>

      <p className="text-foreground/80 text-sm">MP3, OGG или WAV, до {maxMb} МБ каждый.</p>

      {tracks.length === 0 ? (
        <p data-music="empty" className="text-foreground/80 text-sm">
          Пока пусто. Загрузите свои треки — они будут играть на оверлее по очереди.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {tracks.map((track, index) => (
            <li
              key={track.id}
              data-music-row={track.id}
              data-control-row="music-row"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(index));
              }}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                const from = Number(event.dataTransfer.getData('text/plain'));

                if (!Number.isFinite(from) || from === index) {
                  return;
                }

                const ids = tracks.map((item) => item.id);
                const [moved] = ids.splice(from, 1);

                if (moved !== undefined) {
                  ids.splice(index, 0, moved);
                  reorder.mutate(ids);
                }
              }}
              className="border-foreground/10 rounded-lg flex items-center gap-1 border p-1"
            >
              <span className="text-foreground/50 shrink-0" aria-hidden="true">
                <Icon name="drag" size="sm" />
              </span>

              <Button
                variant="ghost"
                size="s"
                className="shrink-0 px-1"
                data-music-play={track.id}
                aria-label={`Играть «${track.name}» в эфире`}
                onClick={() => {
                  onPlay(track.id);
                }}
              >
                <Icon name="play" size="sm" />
              </Button>

              {/* Название - ТЕКСТ, а не поле во всю строку: правится по
                  нажатию, как у эталона. Поле в покое читается как форма. */}
              <Input
                data-music-name={track.id}
                aria-label="Название трека"
                value={track.name}
                onChange={(event) => {
                  rename.mutate({ id: track.id, name: event.target.value });
                }}
                className="min-w-0 flex-1 truncate px-1"
              />

              <Destructive
                id="track-delete"
                className="shrink-0 px-1"
                data={{ 'data-music-remove': track.id }}
                ariaLabel={`Удалить «${track.name}»`}
                icon={<Icon name="delete" size="sm" />}
                title={`Удалить «${track.name}»?`}
                confirm="Удалить"
                onConfirm={() => {
                  remove.mutate(track.id);
                }}
              >
                Трек уйдёт из списка и с нашего сервера. Файл у вас на компьютере останется.
              </Destructive>
            </li>
          ))}
        </ul>
      )}

      {rights && (
        <Modal
          open
          onClose={() => {
            setDismissed(true);
            seen.mutate(undefined);
          }}
          title="Права на музыку"
          actions={
            <Button
              variant="primary"
              data-music="rights-ok"
              onClick={() => {
                setDismissed(true);
                seen.mutate(undefined);
              }}
            >
              Понятно
            </Button>
          }
        >
          <p className="text-sm">
            Музыка в эфире — ваша зона ответственности: TikTok глушит звук и ограничивает показы за
            чужие треки. Загружайте то, на что у вас есть права, или берите свободные лицензии. Это
            сообщение показывается один раз.
          </p>
        </Modal>
      )}
    </div>
  );
}
